import { debounce } from "lodash";
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import apiFactory from "../api";
import { STORAGE } from "../config/Constant";

const groupMimeTypes = (mimeTypes = []) => {
  const options = [];
  const added = new Set();

  const push = (label, value) => {
    if (!added.has(value)) {
      options.push({ label, value });
      added.add(value);
    }
  };

  push("🖼️ All images", "image/");
  push("🎥 All videos", "video/");
  push("🎵 All audios", "audio/");
  push("📄 PDF", "application/pdf");
  push("📝 Word (DOC/DOCX)", "application/msword");
  push("📊 Excel (XLS/XLSX)", "application/vnd.ms-excel");
  push("📑 CSV", "text/csv");
  push("📽️ PowerPoint", "application/vnd.ms-powerpoint");
  push("📦 ZIP / RAR", "application/zip");
  push("💻 Text (.txt)", "text/plain");
  push("💻 JSON", "application/json");
  push("💻 HTML", "text/html");
  push("💻 JavaScript", "application/javascript");
  push("❓ Unknown", "unknown");

  return options;
};

const StorageContext = createContext(null);
export const useStorageContext = () => {
  return useContext(StorageContext);
};

export const StorageProvider = ({ children }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [storageSearch, setStorageSearch] = useState({
    fileName: "",
    name: "",
    mediaType: null,
    conversationType: null,
    dateType: null,
    selectedDate: null,
    fromDate: null,
    toDate: null,
  });

  const [mediaTypes, setMediaType] = useState([]);

  const [pagination, setPagination] = useState({
    page: 1,
    total: 0,
  });

  const [resourceList, setResourceList] = useState([]);

  const fetchResourceList = async () => {
    setIsLoading(true);
    const result = await apiFactory.resourceApi.getStorage({
      search: {
        ...storageSearch,
      },
      page: pagination?.page,
      limit: STORAGE.STORAGE_LIMIT,
    });
    if (result?.status !== 200) return;

    setResourceList(result?.data?.items);
    setPagination({ ...pagination, total: result?.data?.totalItems });
    setIsLoading(false);
  };

  const initResourceList = async () => {
    setIsLoading(true);
    const result = await apiFactory.resourceApi.getStorage({
      search: {
        ...storageSearch,
      },
      page: 1,
      limit: STORAGE.STORAGE_LIMIT,
    });
    if (result?.status !== 200) return;

    setResourceList(result?.data?.items);
    setPagination({ page: 1, total: result?.data?.totalItems });
    setIsLoading(false);
  };

  const viewMore = async () => {
    const result = await apiFactory.resourceApi.getStorage({
      search: {
        ...storageSearch,
      },
      page: pagination?.page + 1,
      limit: STORAGE.STORAGE_LIMIT,
    });
    if (result?.status !== 200) return;

    setResourceList([...resourceList, ...result?.data?.items]);
    setPagination({
      ...pagination,
      page: pagination?.page + 1,
    });
  };

  const searchFileName = debounce((fileName) => {
    resetPagination();
    setStorageSearch((prev) => ({
      ...prev,
      name: fileName,
    }));
  }, 500);

  const onChangeSearch = (e) => {
    const fileName = e.target.value.trim();
    setStorageSearch((prev) => ({
      ...prev,
      fileName: fileName,
    }));
    searchFileName(fileName);
  };

  const resetPagination = () => {
    setPagination({
      page: 1,
      total: 0,
    });
  };

  const onChangeDateType = (value) => {
    const isNotDateTypeChanged =
      (value === "DATE" &&
        !storageSearch?.fromDate &&
        !storageSearch?.toDate) ||
      (value === "RANGE" && !storageSearch?.selectedDate) ||
      (!value &&
        !storageSearch?.selectedDate &&
        !storageSearch?.fromDate &&
        !storageSearch?.toDate);

    if (!isNotDateTypeChanged) {
      resetPagination();
    }

    setStorageSearch((prev) => ({
      ...prev,
      dateType: value,
      selectedDate: null,
      fromDate: null,
      toDate: null,
    }));
  };

  const getContentTypeFile = async () => {
    try {
      const rs = await apiFactory.resourceApi.getActContentType();

      if (rs?.status === 200) {
        const groupedOptions = groupMimeTypes(rs.data);

        setMediaType(groupedOptions);
      }
    } catch (error) {}
  };

  useEffect(() => {
    getContentTypeFile();
  }, []);

  useEffect(() => {
    fetchResourceList();
  }, [
    storageSearch?.conversationType,
    storageSearch?.name,
    storageSearch?.selectedDate,
    storageSearch?.fromDate,
    storageSearch?.toDate,
    storageSearch?.mediaType,
  ]);

  const values = useMemo(
    () => ({
      isLoading,
      storageSearch,
      setStorageSearch,
      setPagination,
      resetPagination,
      resourceList,
      setResourceList,
      pagination,
      viewMore,
      onChangeSearch,
      onChangeDateType,
      initResourceList,
      mediaTypes,
    }),
    [storageSearch, resourceList, pagination, isLoading, mediaTypes]
  );

  return (
    <StorageContext.Provider value={values}>{children}</StorageContext.Provider>
  );
};
